import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import Grid from '@mui/system/Unstable_Grid';
import { Stack, Button } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { 
  useGetPlacementDocumentsQuery, 
  useRemovePlacementDocumentMutation 
} from 'src/services/applications/applications.service';

import { useTable } from 'src/components/table';
import FileManagerTable from 'src/components/documents/file-manager-table';

import PlacementDocumentUploadModal from '../modals/placement-document-uploader';

const DEFAULT_PAGE = {
  page: 1,
  per_page: 10,
};

type Props = {
  placementId?: string;
  uploadVisible?: boolean;
};

export default function PlacementDocumentsView({ placementId, uploadVisible = true }: Props) {
  const [page, setPage] = useState(DEFAULT_PAGE);

  const showUploadModal = useBoolean();

  const { t } = useTranslate();

  const [removeDocument, { isLoading: isRemoving }] = useRemovePlacementDocumentMutation();

  const { currentData: placementDocuments, isLoading } = useGetPlacementDocumentsQuery(
    {
      placementId: placementId as string,
      params: page,
    },
    { skip: !placementId }
  );

  const table = useTable();

  const notFound = !placementDocuments?.count && !isLoading;

  const onAddSuccess = () => {
    showUploadModal.onFalse();
  };

  const handleDeleteItem = async (id: string) => {
    try {
      await removeDocument({
        placementId: placementId as string,
        doc_id: id
      }).unwrap();

      enqueueSnackbar(t('jobs.detail.placements.documents.delete_success'), { variant: 'success' });

    } catch (error) {
      enqueueSnackbar(t('jobs.detail.placements.documents.delete_error'), { variant: 'error' });
    }
  };

  return (
    <Grid container spacing={3}>
      {
        uploadVisible && (
          <Grid xs={12}>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" disabled={!placementId} onClick={showUploadModal.onTrue}>
                {t('profile.documents.upload')}
              </Button>
            </Stack>
          </Grid>
        )
      }
      <Grid xs={12}>
        <Card>
          <FileManagerTable
            table={table}
            dataFiltered={placementDocuments?.results ?? []}
            count={placementDocuments?.count ?? 0}
            onDeleteRow={handleDeleteItem}
            notFound={notFound}
            documentType="company_profile"
            onChangePage={(_e, pageNumber) => setPage({ ...page, page: pageNumber + 1 })}
            onChangePerPage={(e) => setPage({ ...page, per_page: Number(e.target.value) })}
            pageOptions={page}
          />
        </Card>
      </Grid>

      {
        placementId && (
          <PlacementDocumentUploadModal
            open={showUploadModal.value}
            onClose={showUploadModal.onFalse}
            placementId={placementId}
            onUpload={onAddSuccess}
          />
        )
      }
    </Grid>
  );
}